/* ============================================================
   export.js — Real export pipelines
     - downloadJson(scenario)            → .json
     - importJsonFile(file) → scenario   (Promise)
     - downloadPdf({ scenario, slots, rowSummary, mapDataUrl })
     - snapshotMapPng(mapInstance) → Promise<dataUrl>
     - downloadPng(dataUrl, filename)
   ============================================================ */

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';
import { DEFAULT_SETTINGS } from './data.js';
import { sampleWaypoints } from './geometry.js';

// Spacing between exported geo-waypoints along row and lane centerlines.
const WAYPOINT_STEP_M = 10;

// Attach waypoints to each row + lane without mutating the input.
function annotateWithWaypoints(scenario) {
  if (!scenario) return scenario;
  return {
    ...scenario,
    rows: (scenario.rows || []).map((r) => ({
      ...r,
      waypoints: sampleWaypoints(r.start, r.end, WAYPOINT_STEP_M),
    })),
    lanes: (scenario.lanes || []).map((l) => ({
      ...l,
      waypoints: sampleWaypoints(l.start, l.end, WAYPOINT_STEP_M),
    })),
  };
}

// ---------- helpers ----------
function safeFilename(name) {
  return (name || 'parkplatz')
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-zA-Z0-9-_]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase() || 'parkplatz';
}

function todayStamp() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Give the browser a tick to start the download before revoking.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ============================================================
// JSON
// ============================================================
const JSON_FORMAT = 'parkplatz-editor-scenario/v1';

export function downloadJson(scenario) {
  if (!scenario) return;
  const payload = {
    format: JSON_FORMAT,
    exportedAt: new Date().toISOString(),
    waypointStepM: WAYPOINT_STEP_M,
    scenario: annotateWithWaypoints(scenario),
  };
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  triggerDownload(blob, `${safeFilename(scenario.name)}-${todayStamp()}.json`);
}

export async function importJsonFile(file) {
  if (!file) throw new Error('Keine Datei ausgewählt.');
  const text = await file.text();
  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch (e) {
    throw new Error('Ungültige JSON-Datei.');
  }
  // Accept either { scenario: {...} } or a bare scenario object.
  const sc = parsed && parsed.scenario ? parsed.scenario : parsed;
  if (!sc || typeof sc !== 'object') throw new Error('JSON enthält kein Szenario.');
  const required = ['boundaries', 'rows', 'lanes', 'obstacles'];
  for (const k of required) {
    if (!Array.isArray(sc[k])) throw new Error(`Feld "${k}" fehlt oder ist kein Array.`);
  }
  if (!Array.isArray(sc.center) || sc.center.length !== 2) {
    throw new Error('Feld "center" muss [lng, lat] sein.');
  }
  // Re-id so it doesn't collide with an existing scenario in state. Fill in
  // settings defaults for older exports that predate per-scenario settings.
  return {
    ...sc,
    id: 'sc-imp-' + Date.now().toString(36) + '-' + Math.floor(Math.random() * 1e4).toString(36),
    entries: Array.isArray(sc.entries) ? sc.entries : [],
    settings: { ...DEFAULT_SETTINGS, ...(sc.settings || {}) },
    name: sc.name ? `${sc.name} (importiert)` : 'Importiertes Szenario',
  };
}

// ============================================================
// PDF
// ============================================================
export function downloadPdf({ scenario, slots, rowSummary, mapSnapshot, pageOneMap, entries = [] }) {
  // mapSnapshot   — landscape page 5 (plain, no vignette).
  // pageOneMap    — portrait page 1 (vignetted around the parking bbox).
  //                 Falls back to mapSnapshot when not provided.
  const pageOne = pageOneMap || mapSnapshot;
  if (!scenario) return;

  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const margin = 14;

  const totalSlots = slots.length;
  const blockedCount = slots.filter((s) => s.blocked).length;
  const effective = totalSlots - blockedCount;
  const accessible = slots.filter((s) => s.accessible).length;

  // ---- shared header / footer helpers ----
  // Both read page dimensions from the *current* page so they work for both
  // portrait and landscape pages without ad-hoc parameters.
  const drawHeader = (pageNum, totalPages) => {
    const pageW = doc.internal.pageSize.getWidth();
    doc.setDrawColor(216, 199, 173);
    doc.setLineWidth(0.2);
    doc.line(margin, 18, pageW - margin, 18);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(31, 28, 25);
    doc.text(`Parkplatz-Plan · ${scenario.name || 'Standort'}`, margin, 14);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(122, 110, 95);
    doc.text(`Seite ${pageNum} / ${totalPages}`, pageW - margin, 14, { align: 'right' });
  };

  const drawFooter = () => {
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(122, 110, 95);
    doc.text(`Erstellt am ${new Date().toLocaleString('de-DE')}`, margin, pageH - 8);
    doc.text('Parkplatz-Editor · Online Reitschule', pageW - margin, pageH - 8, { align: 'right' });
  };

  const totalPages = 5;
  let pageNum = 1;

  // =========================================================
  // PAGE 1 — Overview
  // =========================================================
  drawHeader(pageNum, totalPages);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.setTextColor(31, 28, 25);
  doc.text('Übersicht', margin, 32);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(122, 110, 95);
  // Subtitle mirrors the modal preview: project name · event date · plan
  // stamp. Falls back to the geocoded address when settings are blank.
  const subtitle = (() => {
    const s = scenario.settings || {};
    const fmtDate = (iso) => {
      if (!iso) return null;
      const d = new Date(iso + 'T00:00:00');
      if (Number.isNaN(d.getTime())) return iso;
      return d.toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' });
    };
    const parts = [s.projectName, fmtDate(s.eventDate), `Plan-Stand ${new Date().toLocaleDateString('de-DE')}`].filter(Boolean);
    return parts.length > 1 ? parts.join(' · ') : (scenario.address || parts[0] || '');
  })();
  if (subtitle) {
    doc.text(subtitle, margin, 38);
  }

  let y = 48;
  // KPI cards
  const cards = [
    { label: 'Gesamtplätze', value: String(totalSlots) },
    { label: 'Effektiv',     value: String(effective) },
    { label: 'Blockiert',    value: String(blockedCount) },
    { label: 'Barrierefrei', value: String(accessible) },
    { label: 'Reihen',       value: String(scenario.rows.length) },
    { label: 'Hindernisse',  value: String(scenario.obstacles.length) },
  ];
  const cardW = (W - margin * 2 - 6 * 2) / 3;
  const cardH = 18;
  cards.forEach((c, i) => {
    const col = i % 3;
    const row = Math.floor(i / 3);
    const x = margin + col * (cardW + 6);
    const yy = y + row * (cardH + 6);
    doc.setDrawColor(216, 199, 173);
    doc.setFillColor(251, 246, 238); // --cream
    doc.roundedRect(x, yy, cardW, cardH, 1.5, 1.5, 'FD');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(122, 110, 95);
    doc.text(c.label.toUpperCase(), x + 3, yy + 5);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(31, 28, 25);
    doc.text(c.value, x + 3, yy + 13);
  });
  y += (cardH + 6) * 2 + 4;

  // Map snapshot — fit-contain inside the available rect, preserving aspect.
  if (pageOne && pageOne.dataUrl) {
    try {
      const availW = W - margin * 2;
      const availH = H - y - 22;
      const srcRatio = pageOne.width / pageOne.height;
      const availRatio = availW / availH;
      let drawW, drawH;
      if (srcRatio > availRatio) {
        drawW = availW;
        drawH = availW / srcRatio;
      } else {
        drawH = availH;
        drawW = availH * srcRatio;
      }
      const drawX = margin + (availW - drawW) / 2;
      const drawY = y + (availH - drawH) / 2;
      doc.setDrawColor(216, 199, 173);
      doc.rect(drawX, drawY, drawW, drawH);
      doc.addImage(pageOne.dataUrl, 'PNG', drawX, drawY, drawW, drawH, undefined, 'FAST');
    } catch (e) {
      console.warn('PDF: could not embed map snapshot', e);
    }
  } else {
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(9);
    doc.setTextColor(122, 110, 95);
    doc.text(
      'Karten-Snapshot nicht verfügbar. Erzeuge die Karte als PNG und lege sie der Mappe bei.',
      margin, y + 8,
    );
  }

  drawFooter();

  // =========================================================
  // PAGE 2 — Row summary table
  // =========================================================
  doc.addPage();
  pageNum = 2;
  drawHeader(pageNum, totalPages);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.setTextColor(31, 28, 25);
  doc.text('Reihen-Übersicht', margin, 32);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(122, 110, 95);
  doc.text('Slot-Anzahl, Winkel und Aufstellung pro Reihe.', margin, 38);

  const tableBody = rowSummary.map((r) => [
    r.label,
    String(r.slots),
    `${Math.round(r.angle)}°`,
    angleLabel(r.angle),
    sideLabel(r.side),
    String(r.accessible),
    String(r.blocked),
  ]);

  // Sum row
  const sumSlots = rowSummary.reduce((s, r) => s + r.slots, 0);
  const sumAcc   = rowSummary.reduce((s, r) => s + r.accessible, 0);
  const sumBlk   = rowSummary.reduce((s, r) => s + r.blocked, 0);
  tableBody.push(['Σ', String(sumSlots), '—', '—', '—', String(sumAcc), String(sumBlk)]);

  autoTable(doc, {
    startY: 44,
    margin: { left: margin, right: margin },
    head: [['Reihe', 'Slots', 'Winkel', 'Typ', 'Seiten', 'BF', 'Blockiert']],
    body: tableBody,
    theme: 'plain',
    styles: {
      font: 'helvetica',
      fontSize: 9,
      cellPadding: 2,
      textColor: [31, 28, 25],
      lineColor: [216, 199, 173],
      lineWidth: 0.1,
    },
    headStyles: {
      fillColor: [245, 236, 223], // --paper
      textColor: [122, 110, 95],
      fontStyle: 'bold',
      fontSize: 8,
      cellPadding: 2.5,
    },
    columnStyles: {
      0: { fontStyle: 'bold' },
      1: { halign: 'right' },
      2: { halign: 'right' },
      5: { halign: 'right' },
      6: { halign: 'right' },
    },
    didParseCell: (data) => {
      // Last (sum) row: bold, top border
      if (data.row.index === tableBody.length - 1) {
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.lineWidth = { top: 0.4, bottom: 0, left: 0, right: 0 };
      }
    },
  });

  drawFooter();

  // =========================================================
  // PAGE 3 — Details (entries, lanes, obstacles)
  // =========================================================
  doc.addPage();
  pageNum = 3;
  drawHeader(pageNum, totalPages);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.setTextColor(31, 28, 25);
  doc.text('Details', margin, 32);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(122, 110, 95);
  doc.text('Ein-/Ausfahrten, Fahrgassen und Hindernisse.', margin, 38);

  // Ein-/Ausfahrten
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(31, 28, 25);
  doc.text('Ein-/Ausfahrten', margin, 50);

  const entryRows = (scenario.entries || []).map((e, i) => [
    `#${i + 1}`,
    e.label || 'Ein/Aus',
    e.position ? `${e.position[1].toFixed(5)}° N` : '—',
    e.position ? `${e.position[0].toFixed(5)}° E` : '—',
  ]);

  if (entryRows.length === 0) {
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(9);
    doc.setTextColor(122, 110, 95);
    doc.text('Keine Ein-/Ausfahrten gesetzt.', margin, 58);
  }

  autoTable(doc, {
    startY: 54,
    margin: { left: margin, right: margin },
    head: entryRows.length ? [['#', 'Bezeichnung', 'Breitengrad', 'Längengrad']] : [],
    body: entryRows,
    theme: 'plain',
    styles: { font: 'helvetica', fontSize: 9, cellPadding: 2, textColor: [31, 28, 25], lineColor: [216, 199, 173], lineWidth: 0.1 },
    headStyles: { fillColor: [245, 236, 223], textColor: [122, 110, 95], fontStyle: 'bold', fontSize: 8 },
    columnStyles: { 0: { fontStyle: 'bold' }, 2: { halign: 'right' }, 3: { halign: 'right' } },
  });

  // Fahrgassen
  let afterY = doc.lastAutoTable?.finalY || 60;
  if (entryRows.length === 0) afterY = 64;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(31, 28, 25);
  doc.text('Fahrgassen', margin, afterY + 10);

  const laneRows = (scenario.lanes || []).map((l, i) => [
    `${i + 1}`,
    `${(l.width ?? 0).toFixed(1)} m`,
    l.direction === 'one-way' ? 'Einbahn' : 'Zwei-Wege',
  ]);

  if (laneRows.length === 0) {
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(9);
    doc.setTextColor(122, 110, 95);
    doc.text('Keine Fahrgassen gezeichnet.', margin, afterY + 18);
  }

  autoTable(doc, {
    startY: afterY + 14,
    margin: { left: margin, right: margin },
    head: laneRows.length ? [['#', 'Breite', 'Richtung']] : [],
    body: laneRows,
    theme: 'plain',
    styles: { font: 'helvetica', fontSize: 9, cellPadding: 2, textColor: [31, 28, 25], lineColor: [216, 199, 173], lineWidth: 0.1 },
    headStyles: { fillColor: [245, 236, 223], textColor: [122, 110, 95], fontStyle: 'bold', fontSize: 8 },
    columnStyles: { 0: { fontStyle: 'bold' }, 1: { halign: 'right' } },
  });

  // Hindernisse + Flächen
  let obY = doc.lastAutoTable?.finalY || (afterY + 20);
  if (laneRows.length === 0) obY = afterY + 24;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(31, 28, 25);
  doc.text('Hindernisse & Flächen', margin, obY + 10);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(31, 28, 25);
  const boundCount = (scenario.boundaries || []).length;
  const obsCount   = (scenario.obstacles  || []).length;
  doc.text(`${boundCount} ${boundCount === 1 ? 'Fläche' : 'Flächen'} · ${obsCount} ${obsCount === 1 ? 'Hindernis' : 'Hindernisse'}`, margin, obY + 18);

  drawFooter();

  // =========================================================
  // PAGE 4 — Waypoints (every WAYPOINT_STEP_M m along row/lane centerlines)
  // =========================================================
  doc.addPage();
  pageNum = 4;
  drawHeader(pageNum, totalPages);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.setTextColor(31, 28, 25);
  doc.text('Wegpunkte', margin, 32);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(122, 110, 95);
  doc.text(
    `Alle ${WAYPOINT_STEP_M} m entlang jeder Reihe und Fahrgasse — zum Einmessen vor Ort.`,
    margin, 38,
  );

  const wpRows = (scenario.rows || []).map((r) => ({
    kind: 'Reihe',
    label: r.label || '—',
    waypoints: sampleWaypoints(r.start, r.end, WAYPOINT_STEP_M),
  }));
  const wpLanes = (scenario.lanes || []).map((l, i) => ({
    kind: 'Fahrgasse',
    label: `${i + 1}`,
    waypoints: sampleWaypoints(l.start, l.end, WAYPOINT_STEP_M),
  }));
  const wpItems = [...wpRows, ...wpLanes];

  if (wpItems.length === 0) {
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(9);
    doc.setTextColor(122, 110, 95);
    doc.text('Noch keine Reihen oder Fahrgassen vorhanden.', margin, 50);
  } else {
    // Build one wide multi-section table: subheader rows alternate with
    // waypoint rows. Lets autotable handle pagination across the printed page.
    const body = [];
    for (const item of wpItems) {
      body.push([
        { content: `${item.kind} ${item.label}`, colSpan: 4,
          styles: { fontStyle: 'bold', fillColor: [245, 236, 223], textColor: [31, 28, 25] } },
      ]);
      item.waypoints.forEach((wp, idx) => {
        body.push([
          String(idx + 1),
          `${wp.distM.toFixed(1)} m`,
          `${wp.lat.toFixed(5)}° N`,
          `${wp.lng.toFixed(5)}° E`,
        ]);
      });
    }
    autoTable(doc, {
      startY: 44,
      margin: { left: margin, right: margin },
      head: [['Nr.', 'Distanz', 'Breitengrad', 'Längengrad']],
      body,
      theme: 'plain',
      styles: { font: 'helvetica', fontSize: 8, cellPadding: 1.8, textColor: [31, 28, 25], lineColor: [216, 199, 173], lineWidth: 0.1 },
      headStyles: { fillColor: [251, 246, 238], textColor: [122, 110, 95], fontStyle: 'bold', fontSize: 7 },
      columnStyles: {
        0: { halign: 'right', cellWidth: 14 },
        1: { halign: 'right', cellWidth: 24 },
        2: { halign: 'right' },
        3: { halign: 'right' },
      },
    });
  }

  drawFooter();

  // =========================================================
  // PAGE 5 — Landscape full-page map
  // =========================================================
  doc.addPage('a4', 'landscape');
  pageNum = 5;
  drawHeader(pageNum, totalPages);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(31, 28, 25);
  doc.text('Lageplan', margin, 32);

  if (mapSnapshot && mapSnapshot.dataUrl) {
    try {
      const lW = doc.internal.pageSize.getWidth();
      const lH = doc.internal.pageSize.getHeight();
      // Fill below the header, above the footer.
      const availX = margin;
      const availY = 38;
      const availW = lW - margin * 2;
      const availH = lH - availY - 14;
      const srcRatio = mapSnapshot.width / mapSnapshot.height;
      const availRatio = availW / availH;
      let drawW, drawH;
      if (srcRatio > availRatio) {
        drawW = availW;
        drawH = availW / srcRatio;
      } else {
        drawH = availH;
        drawW = availH * srcRatio;
      }
      const drawX = availX + (availW - drawW) / 2;
      const drawY = availY + (availH - drawH) / 2;
      doc.setDrawColor(216, 199, 173);
      doc.rect(drawX, drawY, drawW, drawH);
      doc.addImage(mapSnapshot.dataUrl, 'PNG', drawX, drawY, drawW, drawH, undefined, 'FAST');
    } catch (e) {
      console.warn('PDF: could not embed landscape map snapshot', e);
    }
  } else {
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(10);
    doc.setTextColor(122, 110, 95);
    doc.text('Karten-Snapshot nicht verfügbar.', margin, 50);
  }

  drawFooter();

  // ---- Save ----
  doc.save(`${safeFilename(scenario.name)}-${todayStamp()}.pdf`);
}

function angleLabel(a) {
  if (a <= 0.5) return 'Längs';
  if (a <= 32)  return 'Flach';
  if (a <= 50)  return 'Fischgräten';
  if (a <= 68)  return 'Schräg';
  if (a <= 80)  return 'Steil';
  return 'Senkrecht';
}

function sideLabel(side) {
  if (side === 'both')  return 'beidseitig';
  if (side === 'left')  return 'links';
  return 'rechts';
}

// ============================================================
// PNG (live map snapshot)
// ============================================================
// Rasterises the #map div as it's currently rendered. Reuses tiles already
// loaded in the DOM — no re-fetching against the tile server.
// Returns the dataURL along with the canvas's pixel dimensions so callers
// (e.g. PDF embed) can preserve aspect ratio without re-decoding.
export async function snapshotMapPng(mapElement) {
  if (!mapElement) throw new Error('Karte ist nicht bereit.');
  const canvas = await html2canvas(mapElement, {
    useCORS: true,
    allowTaint: false,
    backgroundColor: '#0a0a0c',
    scale: window.devicePixelRatio > 1 ? 1.5 : 1,
    logging: false,
    ignoreElements: (el) => el.classList?.contains('leaflet-control-attribution'),
  });
  return {
    dataUrl: canvas.toDataURL('image/png'),
    width: canvas.width,
    height: canvas.height,
  };
}

// Crops the snapshot down to the parking-area bbox (with padding), so page 1
// shows just the area of interest instead of the whole tile extent. The
// snapshot's `bboxPx` is in pre-scale CSS pixels of the source map element;
// the snapshot canvas is `scale`× that. Caller-facing API:
//
//   cropToBbox({ dataUrl, width, height, bboxPx }, mapElement, opts)
//
// `mapElement` is needed only to figure out the scale factor (snapshot
// width / element clientWidth). Returns { dataUrl, width, height }.
// Overlay small numbered circles at each waypoint position on a snapshot.
// Preserves snapshot shape ({ dataUrl, width, height, bboxPx, waypointsPx })
// so the result can still be cropped afterwards.
export async function overlayWaypoints(snapshot, mapElement, opts = {}) {
  if (!snapshot?.dataUrl || !Array.isArray(snapshot?.waypointsPx) || !mapElement) return snapshot;
  if (snapshot.waypointsPx.length === 0) return snapshot;

  const elW = mapElement.clientWidth || snapshot.width;
  const scale = snapshot.width / elW;
  const dotRadius = opts.dotRadius ?? Math.max(5, 6 * scale);
  const fontSize = opts.fontSize ?? Math.max(8, 8 * scale);

  const img = await new Promise((resolve, reject) => {
    const im = new Image();
    im.onload = () => resolve(im);
    im.onerror = reject;
    im.src = snapshot.dataUrl;
  });

  const canvas = document.createElement('canvas');
  canvas.width = snapshot.width;
  canvas.height = snapshot.height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0);

  // Group by parent so we can use distinct colours per row/lane and reset the
  // sequential number for each.
  const ROW_COLOR = '#1f1c19';
  const LANE_COLOR = '#7a3f1d';

  ctx.font = `600 ${fontSize}px ${'helvetica, sans-serif'}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  for (const wp of snapshot.waypointsPx) {
    const x = wp.x * scale;
    const y = wp.y * scale;
    const isLane = wp.kind === 'lane';

    // Halo first
    ctx.beginPath();
    ctx.arc(x, y, dotRadius + 1.4, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(255,255,255,0.85)';
    ctx.fill();
    // Coloured dot
    ctx.beginPath();
    ctx.arc(x, y, dotRadius, 0, Math.PI * 2);
    ctx.fillStyle = isLane ? LANE_COLOR : ROW_COLOR;
    ctx.fill();
    // Index
    ctx.fillStyle = '#fff';
    ctx.fillText(String(wp.idx), x, y + 0.5);
  }

  return {
    ...snapshot,
    dataUrl: canvas.toDataURL('image/png'),
    width: canvas.width,
    height: canvas.height,
  };
}

export async function cropToBbox(snapshot, mapElement, opts = {}) {
  if (!snapshot?.dataUrl || !snapshot?.bboxPx || !mapElement) return snapshot;
  const padding = opts.padding ?? 0.15; // 15% of bbox on each side as breathing room

  const elW = mapElement.clientWidth || snapshot.width;
  const scale = snapshot.width / elW;

  const padX = snapshot.bboxPx.w * padding * scale;
  const padY = snapshot.bboxPx.h * padding * scale;
  const cx = Math.max(0, snapshot.bboxPx.x * scale - padX);
  const cy = Math.max(0, snapshot.bboxPx.y * scale - padY);
  const cw = Math.min(snapshot.width  - cx, snapshot.bboxPx.w * scale + padX * 2);
  const ch = Math.min(snapshot.height - cy, snapshot.bboxPx.h * scale + padY * 2);

  // Degenerate bbox (e.g. single point) → fall back to the original snapshot
  // rather than producing an empty crop.
  if (cw < 4 || ch < 4) return snapshot;

  const img = await new Promise((resolve, reject) => {
    const im = new Image();
    im.onload = () => resolve(im);
    im.onerror = reject;
    im.src = snapshot.dataUrl;
  });

  const canvas = document.createElement('canvas');
  canvas.width = Math.round(cw);
  canvas.height = Math.round(ch);
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, cx, cy, cw, ch, 0, 0, cw, ch);

  return {
    dataUrl: canvas.toDataURL('image/png'),
    width: canvas.width,
    height: canvas.height,
  };
}

export function downloadPng(snapshot, scenario) {
  // Accepts either a bare dataURL string or { dataUrl, width, height } so it
  // remains compatible with both old and new callers.
  const dataUrl = typeof snapshot === 'string' ? snapshot : snapshot?.dataUrl;
  if (!dataUrl) return;
  const byteString = atob(dataUrl.split(',')[1]);
  const mime = dataUrl.match(/data:(.*?);/)[1] || 'image/png';
  const buf = new Uint8Array(byteString.length);
  for (let i = 0; i < byteString.length; i++) buf[i] = byteString.charCodeAt(i);
  const blob = new Blob([buf], { type: mime });
  triggerDownload(blob, `${safeFilename(scenario?.name)}-${todayStamp()}.png`);
}
